import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  ContentBox,
  ContentPage,
  DrawerFilterPanel,
  DropdownAction,
  FullTable,
  NoResults,
  Notice,
  PageActions,
  Select,
  TableFilters,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import { hasPermission } from "@helpers/permissions";
import { documentMeta, downloadDocument } from "@helpers/documentFile";
import { escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import { labelWithHint, searchLabelAndHint } from "@components/OptionWithHint";
import DocumentFormModal from "./DocumentFormModal";
import DeleteDocumentModal from "./DeleteDocumentModal";

// Documentos da empresa (modelo Lista do DS OneWork). O ADM envia, edita e exclui;
// com "Ver documentos", o colaborador só lista e baixa. Com quem cada documento foi
// compartilhado (coluna e filtro) exige também "Ver colaboradores": sem ela, o back
// nem manda `sharedUsers`.

// Uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 25;

const BREADCRUMB = [{ page: "Documentos", route: "/c/documents" }];

const EMPTY = { totalRegisters: 0, list: [], totalPages: 1 };

// Até 2 nomes na linha; acima disso, "Fulano e mais N" com todos no Tooltip
const SHARED_NAMES = 2;

const NoticeSpace = styled.div`
  margin-bottom: 20px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

// O ButtonLink da lib não quebra linha: nome longo corta com reticências
const FileName = styled.span`
  display: block;
  max-width: 100%;
  min-width: 0;

  button {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
  }
`;

// O nome baixa sozinho: sem isso, o clique na linha baixaria de novo
const stopRowClick = (event) => event.stopPropagation();

/** Pessoas (`users?role=EMP`) → opções do Select: o nome no campo e o e-mail no menu. */
const userOptionsOf = (users) =>
  users.map((u) => ({ value: u.id, label: u.name || u.email || `Colaborador #${u.id}`, hint: u.name ? u.email || "" : "" }));

export default function Documents() {
  const documentsService = useService("documents");
  const userService = useService("users");
  const auth = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const isAdmin = auth?.role === "ADM";
  const canSeeSharing = isAdmin || hasPermission("users:read");

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // null enquanto carrega
  const [userOptions, setUserOptions] = useState(canSeeSharing ? null : []);
  // { mode: "create" | "edit" | "delete", doc }
  const [modal, setModal] = useState(null);
  // Só a resposta da última requisição vale (filtros seguidos podem voltar fora de ordem)
  const lastRequest = useRef(0);

  const {
    storeFilters,
    filters,
    filter,
    openFilter,
    setOpenFilter,
    handleSetFilters,
    handleConfirmFilter,
    handleClearFilters,
    hasFilter,
  } = useListFilters({}, setPagination);

  usePageHeader({ title: "Documentos", breadcrumb: BREADCRUMB });

  const searchTerm = (filters.search || "").trim();
  const usersValue = canSeeSharing ? filters.users || "" : "";

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, searchTerm, usersValue]);

  useEffect(() => {
    if (canSeeSharing) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchUsers() {
    try {
      const result = await userService.list("role=EMP&limit=9999&order=name:ASC");
      if (!result?.data?.list) throw new Error("Erro ao carregar os colaboradores");
      setUserOptions(userOptionsOf(result.data.list));
    } catch (error) {
      toast.error("Erro ao carregar os colaboradores");
      setUserOptions([]);
    }
  }

  function buildQuery() {
    // Visão da empresa: sem scope=company o back devolve só os compartilhados com quem chama
    const params = ["scope=company", `page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
    if (searchTerm) params.push(`filename=${encodeURIComponent(searchTerm)}`);
    if (usersValue) params.push(`sharedUsers=${usersValue}`);
    return params.join("&");
  }

  async function fetchDocuments() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await documentsService.list(buildQuery());
      if (request !== lastRequest.current) return;
      if (!result?.data?.list) throw new Error(typeof result === "string" ? result : "Erro ao carregar documentos");
      // Excluir o último da última página deixaria a página vazia
      if (!result.data.list.length && pagination.pageIndex > 0) {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
      setPaginationData(result.data);
    } catch (error) {
      if (request !== lastRequest.current) return;
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar documentos");
      setPaginationData(EMPTY);
    }
    setLoading(false);
  }

  const handleDownload = (doc) => downloadDocument(documentsService, doc);
  const openCreate = () => setModal({ mode: "create" });
  const closeModal = () => setModal(null);

  function afterChange() {
    setModal(null);
    fetchDocuments();
  }

  function renderShared(users) {
    if (!users?.length) {
      return <Text $text="Não compartilhado" $variant="paragraph_small_medium" $color="N400" />;
    }
    const names = users.map((u) => u.name || `Colaborador #${u.id}`);
    if (names.length <= SHARED_NAMES) {
      return <Text $text={escapeHtml(names.join(", "))} $variant="paragraph_small_medium" $color="N900" />;
    }
    return (
      <Tooltip content={names.join(", ")} $position="top" $size="large" $color="dark">
        <Text
          $text={escapeHtml(`${names[0]} e mais ${names.length - 1}`)}
          $variant="paragraph_small_medium"
          $color="N900"
        />
      </Tooltip>
    );
  }

  const columns = [
    {
      id: "document",
      header: "Documento",
      size: 380,
      cell: ({ row }) => {
        const doc = row.original;
        const meta = documentMeta(doc);
        return (
          <Stack>
            <FileName onClick={stopRowClick}>
              <ButtonLink $text={doc.filename || "Documento"} $action={() => handleDownload(doc)} $noPadding $noWidth />
            </FileName>
            {meta ? <Text $text={meta} $variant="caption_medium" $color="N400" /> : null}
          </Stack>
        );
      },
    },
    ...(canSeeSharing
      ? [{ id: "shared", header: "Compartilhado com", size: 280, cell: ({ row }) => renderShared(row.original.sharedUsers) }]
      : []),
    // Os enviados antes de 2026-10-29 não têm data ("—"): o back não a gravava
    { id: "created_at", header: "Enviado em", size: 170, cell: ({ row }) => formatDateTime(row.original.created_at) },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[
            { label: "Baixar", action: () => handleDownload(row.original) },
            { label: "Editar", action: () => setModal({ mode: "edit", doc: row.original }), show: isAdmin },
            {
              label: "Excluir",
              action: () => setModal({ mode: "delete", doc: row.original }),
              show: isAdmin,
              isImportant: true,
            },
          ]}
        />
      ),
    },
  ];

  const filtering = !!hasFilter || !!searchTerm;
  const noResults = filtering ? (
    <NoResults notResultText="Nenhum documento encontrado" includeText="Limpar filtros" includeAction={handleClearFilters} />
  ) : isAdmin ? (
    <NoResults notResultText="Nenhum documento por aqui" includeText="Enviar documento" includeAction={openCreate} />
  ) : (
    <NoResults notResultText="Nenhum documento por aqui" />
  );

  return (
    <ContentPage>
      {isAdmin ? (
        <PageActions>
          <Button $text="Enviar documento" $variant="primary" $action={openCreate} />
        </PageActions>
      ) : null}

      <ContentBox>
        <NoticeSpace>
          <Notice
            $variant="gray"
            $text="Cada colaborador vê, em Documentos, só os documentos compartilhados com ele e recebe um e-mail quando um novo é compartilhado."
          />
        </NoticeSpace>

        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar pelo nome do documento"
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={handleClearFilters}
          setStatePagination={setPagination}
          openFilter={() => setOpenFilter((prev) => !prev)}
          disableFilter={!canSeeSharing}
          disableExport
          hasFilter={hasFilter}
        />

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={handleDownload}
          noResults={noResults}
        />
      </ContentBox>

      {canSeeSharing ? (
        <DrawerFilterPanel
          $showDrawer={openFilter}
          $setShowDrawer={() => setOpenFilter((prev) => !prev)}
          $confirmFilter={handleConfirmFilter}
          $clearFilters={handleClearFilters}
          $hasFilter={hasFilter}
        >
          <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            $idForm="documents-users"
            $label="Compartilhado com"
            $options={userOptions || []}
            $textNoOptions="Nenhum colaborador encontrado"
            isLoading={userOptions === null}
            placeholder="Selecione os colaboradores"
            formatOptionLabel={labelWithHint}
            filterOption={searchLabelAndHint}
            value={(userOptions || []).filter((o) => (filter.users || "").split("|").includes(String(o.value)))}
            onChange={(options) => handleSetFilters(null, "users", (options || []).map((o) => o.value).join("|"))}
          />
        </DrawerFilterPanel>
      ) : null}

      {modal?.mode === "create" || modal?.mode === "edit" ? (
        <DocumentFormModal
          doc={modal.doc}
          userOptions={userOptions}
          onClose={closeModal}
          onSaved={afterChange}
        />
      ) : null}
      {modal?.mode === "delete" ? (
        <DeleteDocumentModal doc={modal.doc} onClose={closeModal} onDeleted={afterChange} />
      ) : null}
    </ContentPage>
  );
}
